<template>
  <div data-testid="device-audit-log" class="flex flex-col h-full">
    <div class="flex items-center justify-between mb-5">
      <h1 class="text-lg font-medium leading-6 text-gray-900">
        {{ $t("event.header.events") }}
      </h1>
      <button v-if="simpleEvents.length" @click="isModalOpen = true" data-testid="audit-log-expand-button" class="p-2 text-gray-500 transition-colors rounded-lg hover:text-gray-700 " :title="$t('common.action.expand') || 'Expand'">
        <ArrowsExpandIcon class="w-5 h-5" />
      </button>
    </div>

    <div ref="scrollContainer" class="flex-1 pb-4 overflow-y-auto">
      <div v-if="hasError && !loading" class="py-8 text-sm text-center text-gray-500">
        {{ $t("event.loadError") || "Could not load events. Please try again later." }}
      </div>

      <div v-else-if="!simpleEvents.length && !loading" class="py-8 text-sm text-center text-gray-500">
        {{ $t("event.noEvents") }}
      </div>


      <ul v-else class="pb-4">
        <li v-for="event in simpleEvents" :key="event.node.id" data-testid="audit-log-entry">
          <div class="relative pb-4">
            <div class="relative flex space-x-3">
              <div>
                <span :class="[
                  getIconBackground(event.node.type),
                  'h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white',
                ]">
                  <component :is="getIcon(event.node.type)" class="w-5 h-5" aria-hidden="true" />
                </span>
              </div>
              <div class="w-full">
                <Disclosure v-slot="{ open }">
                  <DisclosureButton class="flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-gray-900 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus-visible:ring focus-visible:ring-gray-500 focus-visible:ring-opacity-75">
                    <span><b>{{ dateFormat(event.node.time) }}: </b> {{ $t(event.node.type) }}</span>
                    <ChevronUpIcon :class="open ? 'transform ' : 'rotate-180'" class="w-5 h-5 text-gray-500" />
                  </DisclosureButton>
                  <DisclosurePanel class="text-sm text-gray-500">
                    <highlightjs language="json" :code="formatJson(event.node.data)" />
                  </DisclosurePanel>
                </Disclosure>
              </div>
            </div>
          </div>
        </li>
        <li v-if="hasMore && !loading" class="py-4 text-center">
          <button v-if="isMobile" @click="loadMore" class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 focus:outline-none">
            {{ $t("common.action.loadMore") || "Load more" }}
          </button>
          <div v-else ref="loadMoreTrigger" class="text-sm text-gray-500">{{ $t("common.action.loading") || "Loading more..." }}</div>
        </li>
        <li v-if="loading" class="py-4 text-center">
          <div class="inline-block w-6 h-6 border-4 border-blue-500 border-solid rounded-full animate-spin border-r-transparent"></div>
        </li>
      </ul>
    </div>
  </div>

  <DeviceAuditLogModal :is-open="isModalOpen" :device-id="currentDeviceId" @close="isModalOpen = false" />
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { useStore } from "vuex";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import { CheckIcon, ChevronUpIcon } from "@heroicons/vue/solid";
import { InformationCircleIcon, ExclamationIcon, UserIcon, ArrowsExpandIcon } from "@heroicons/vue/outline";
import DeviceAuditLogModal from "@/components/DeviceAuditLogModal";
import { EVENT_TYPE_CATEGORY_MAPPING, SIMPLE_MODE_EVENT_TYPES } from "@/lib/alarms";

export default {
  name: "DeviceAuditLog",
  components: {
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    ChevronUpIcon,
    CheckIcon,
    ExclamationIcon,
    InformationCircleIcon,
    UserIcon,
    ArrowsExpandIcon,
    DeviceAuditLogModal,
  },

  setup() {
    const store = useStore();
    const loadMoreTrigger = ref(null);
    const scrollContainer = ref(null);

    const isModalOpen = ref(false);
    const isMobile = ref(typeof window !== 'undefined' && window.innerWidth < 768);

    const onResize = () => {
      isMobile.value = window.innerWidth < 768;
    };
    const loading = computed(() => store.state.device.loadingEvents);
    const displayedEvents = computed(() => store.state.device.events || []);
    const hasMore = computed(() => store.state.device.eventsHasNextPage);
    const currentDeviceId = computed(() => store.state.device.current.id);
    const simpleEvents = computed(() => displayedEvents.value);
    const hasError = computed(() => store.state.device.eventsError);

    const loadMore = async () => {
      if (loading.value || !hasMore.value || !currentDeviceId.value) {
        return;
      }

      try {
        await store.dispatch('device/getDeviceEvents', {
          deviceId: currentDeviceId.value,
          reload: false,
          eventTypes: SIMPLE_MODE_EVENT_TYPES
        });
      } catch (error) {
        console.error('Failed to load more events:', error);
      }
    };

    let observer = null;

    const setupObserver = async () => {
      if (isMobile.value) return;

      await nextTick();

      if (!observer && scrollContainer.value) {
        observer = new IntersectionObserver(
          (entries) => {

            const first = entries[0];
            if (first.isIntersecting && hasMore.value && !loading.value) {
              loadMore();
            }
          },
          {
            root: scrollContainer.value,
            threshold: 0.1,
            rootMargin: '100px'
          }
        );
      }

      if (loadMoreTrigger.value && observer) {
        observer.observe(loadMoreTrigger.value);
      }
    };

    onMounted(async () => {
      window.addEventListener('resize', onResize);

      if (currentDeviceId.value) {
        try {
          await store.dispatch('device/getDeviceEvents', {
            deviceId: currentDeviceId.value,
            reload: true,
            eventTypes: SIMPLE_MODE_EVENT_TYPES
          });
        } catch (error) {
          console.log("error", error)
        }
      }

      await setupObserver();
    });

    onUnmounted(() => {
      window.removeEventListener('resize', onResize);
      if (observer) {
        observer.disconnect();
      }
    });

    watch(loadMoreTrigger, async (newTrigger, oldTrigger) => {

      if (oldTrigger && observer) {
        observer.unobserve(oldTrigger);
      }
      if (newTrigger && observer) {
        await nextTick();
        observer.observe(newTrigger);
      } else if (newTrigger) {
        await setupObserver();
      }
    });

    watch(scrollContainer, async (newContainer) => {
      if (newContainer && !observer) {
        await setupObserver();
      }
    });

    watch(() => store.state.device.current.id, async (newId, oldId) => {
      if (newId && newId !== oldId) {
        try {
          await store.dispatch('device/getDeviceEvents', {
            deviceId: newId,
            reload: true,
            eventTypes: SIMPLE_MODE_EVENT_TYPES
          });
        } catch (error) {
          console.log("error", error)
        }
      }
    });


    return {
      loadMoreTrigger,
      scrollContainer,
      loading,
      displayedEvents,
      simpleEvents,
      hasMore,
      isModalOpen,
      currentDeviceId,
      hasError,
      isMobile,
      loadMore,
      getIconBackground(event_type) {
        const event_category = EVENT_TYPE_CATEGORY_MAPPING[event_type]
        switch (event_category) {
          case "error":
            return "bg-red-500 text-white";
          case "info":
            return "bg-blue-500 text-white";
          case "user":
            return "bg-yellow-100 text-gray-600";
          case "ok":
            return "bg-green-500 text-white";
        }
        return "bg-blue-500";
      },
      getIcon(event_type) {
        const event_category = EVENT_TYPE_CATEGORY_MAPPING[event_type]
        switch (event_category) {
          case "error":
            return ExclamationIcon;
          case "info":
            return InformationCircleIcon;
          case "user":
            return UserIcon;
          case "ok":
            return CheckIcon;
        }
        return InformationCircleIcon;
      },
      dateFormat(date) {
        return new Date(date).toLocaleString("de-DE");
      },
      formatJson(data) {
        return JSON.stringify(JSON.parse(data), null, 2);
      },
    };
  },
};
</script>
